/* eslint-disable react/jsx-props-no-spreading */
import type {
  ComponentPropsWithoutRef,
  ElementType,
  ForwardRefExoticComponent,
  LegacyRef,
  PropsWithChildren,
  RefAttributes,
} from 'react';
import { forwardRef } from 'react';

import cx from 'classnames';

import styles from './Layers.module.css';

type PolymorphicAsProp<E extends ElementType> = {
  as?: E;
};

type PolymorphicProps<E extends ElementType> = PropsWithChildren<
  ComponentPropsWithoutRef<E> & PolymorphicAsProp<E>
>;

const defaultElement = 'div';

/**
 * Used to "stack" children in the z-index
 * Contains 2 or more Layer children, rendered one over-top of the other
 */
const LayersInner = <Element extends ElementType = typeof defaultElement>(
  { as, children, className, ...props }: PolymorphicProps<Element>,
  ref: LegacyRef<HTMLDivElement> | undefined,
) => {
  const Component = as ?? defaultElement;

  return (
    <Component className={cx(styles.layers, className)} {...props} ref={ref}>
      {children}
    </Component>
  );
};

/**
 * Direct child of `<Layers />`
 */
const Layer = <Element extends ElementType = typeof defaultElement>({
  as,
  children,
  className,
  ...props
}: PolymorphicProps<Element>) => {
  const Component = as ?? defaultElement;

  return (
    <Component className={cx(styles.layer, className)} {...props}>
      {children}
    </Component>
  );
};

type TLayers = ForwardRefExoticComponent<
  Omit<PolymorphicProps<ElementType>, 'ref'> & RefAttributes<HTMLDivElement>
> & { Layer: typeof Layer };

export const Layers: TLayers = forwardRef(LayersInner) as TLayers;

Layers.Layer = Layer;

Layers.displayName = 'Layers';
Layer.displayName = 'Layers.Layer';
